import { GetOrgSettings, GetServicerSettings, GetPublicServicerSettings, GetPublicPartnerSettings } from '../gql/leadGenQueries.gql'
import apolloClient from '~/util/apolloClient'
import axios from 'axios'
import { clearChatInitInflight, getChatInitInflight, setChatInitInflight } from './leadGenChatSession'

const { provider } = apolloClient()

function initialsFromName (fullName) {
  return (fullName || '').split(' ').map(name => name[0]).join('')
}

function mapLeadGenSettings (source) {
  return {
    submissionPageBodyText: source.lead_gen_setting_submission_page_body_text || null,
    prequalHardCreditAllowed: source.lead_gen_setting_prequal_hard_credit_allowed || false,
    purchaseFlow: source.lead_gen_setting_purchase_flow || false,
    refinanceFlow: source.lead_gen_setting_refinance_flow || false,
    prequalFlow: source.lead_gen_setting_prequal_flow || false,
    autoRunLpa: source.lead_gen_setting_auto_run_lpa || false,
    autoRunDu: source.lead_gen_setting_auto_run_du || false,
    autoRunCredit: source.lead_gen_setting_auto_run_credit || false,
    conversationalFlow: source.lead_gen_setting_conversational_flow || false,
    hasAusIntegration: source.has_du || source.has_lpa || false,
    hasDu: source.has_du || false,
    hasLpa: source.has_lpa || false,
    hasCreditIntegration: source.has_hard_credit || source.has_soft_credit || false,
    hasSoftCredit: source.has_soft_credit || false,
    hasHardCredit: source.has_hard_credit || false
  }
}

function mapOrgTermsData (source) {
  return {
    auth_flow_config_setting_require_terms_and_conditions_checkbox: source.auth_flow_config_setting_require_terms_and_conditions_checkbox,
    auth_flow_config_setting_terms_and_conditions_request_html: source.auth_flow_config_setting_terms_and_conditions_request_html,
    has_custom_terms_and_conditions: source.has_custom_terms_and_conditions,
    company_has_data_sale: source.company_has_data_sale,
    feature_setting_econsent_required_at_onboarding: source.feature_setting_econsent_required_at_onboarding,
    borrower_web_content_setting_register_account_t_and_c_link_text: source.borrower_web_content_setting_register_account_t_and_c_link_text,
    borrower_web_content_setting_terms_and_conditions_title_text: source.borrower_web_content_setting_terms_and_conditions_title_text,
    borrower_web_content_setting_terms_and_conditions_econsent_link_text: source.borrower_web_content_setting_terms_and_conditions_econsent_link_text,
    user_loan_app_setting_credit_auth_text: source.user_loan_app_setting_credit_auth_text,
    lead_gen_econsent_modal_text: source.lead_gen_econsent_modal_text
  }
}

export default {
  fetchOrgSettings (context) {
    const orgGuid = context.state.orgGuid
    const orgType = context.state.orgType
    if (!orgType || !orgGuid) return

    return provider.clients.defaultClient.query({
      query: GetOrgSettings,
      variables: { orgGuid, orgType }
    }).then(({ data: { publicData: { organization } } }) => {
      if (organization) {
        context.commit('setSettings', {
          ...mapLeadGenSettings(organization),
          landingPageTitle: organization.lead_gen_setting_landing_page_title_text || null,
          hasGenAiTenant: organization.has_gen_ai_tenant || false,
          orgIcon: organization.effective_logo_url || null,
          orgAppIcon: organization.effective_app_icon_url || null,
          name: organization.name || null,
          orgTermsData: mapOrgTermsData(organization)
        })
      } else {
        context.commit('reinitializeOrgSettings')
      }
    }).catch(() => {
      context.commit('reinitializeOrgSettings')
    })
  },
  fetchServicerSettings (context) {
    const servicerId = context.state.orgId
    if (!servicerId) return

    return provider.clients.defaultClient.query({
      query: GetServicerSettings,
      variables: { id: servicerId }
    }).then(({ data: { servicer_profile } }) => {
      // eslint-disable-next-line babel/camelcase
      if (servicer_profile) {
        context.commit('setSettings', {
          ...mapLeadGenSettings(servicer_profile),
          orgIcon: servicer_profile.effective_company_logo_url,
          orgAppIcon: servicer_profile.effective_app_icon_url,
          servicerImageUrl: servicer_profile.profile_image_url,
          servicerInitials: initialsFromName(servicer_profile.full_name),
          landingPageTitle: servicer_profile.lead_gen_setting_landing_page_title_text,
          hasGenAiTenant: servicer_profile.has_gen_ai_tenant || false
        })
      } else {
        context.commit('reinitializeServicerSettings')
      }
    }).catch(() => {
      context.commit('reinitializeServicerSettings')
    })
  },
  fetchPublicServicerAndPartnerSettings (context) {
    const sac = context.state.sac
    if (!sac) return

    const servicerSettingsPromise = provider.clients.defaultClient.query({
      query: GetPublicServicerSettings,
      variables: { sac }
    }).then(({ data: { publicData: { servicer_profile } } }) => {
      // eslint-disable-next-line babel/camelcase
      if (servicer_profile) {
        context.commit('setSettings', {
          ...mapLeadGenSettings(servicer_profile),
          orgIcon: servicer_profile.effective_company_logo_url,
          orgAppIcon: servicer_profile.effective_app_icon_url,
          servicerImageUrl: servicer_profile.profile_image_url,
          servicerInitials: initialsFromName(servicer_profile.full_name),
          landingPageTitle: servicer_profile.lead_gen_setting_landing_page_title_text,
          hasGenAiTenant: servicer_profile.has_gen_ai_tenant || false
        })
      } else {
        context.commit('reinitializeServicerSettings')
      }
    }).catch(() => {
      context.commit('reinitializeServicerSettings')
    })

    context.dispatch('fetchPublicPartnerSettings', context)

    return servicerSettingsPromise
  },
  fetchPublicPartnerSettings (context) {
    const sac = context.state.sac
    if (!sac) return

    provider.clients.defaultClient.query({
      query: GetPublicPartnerSettings,
      variables: { sac }
    }).then(({ data: { publicData: { partner } } }) => {
      if (partner) {
        context.commit('setPartnerSettings', {
          partnerImageUrl: partner.profile_image_url,
          partnerInitials: initialsFromName(partner.full_name)
        })
        context.commit('setPartner', partner)
      } else {
        context.commit('reinitializePartnerSettings')
      }
    }).catch(() => {
      context.commit('reinitializePartnerSettings')
    })
  },
  clearSacData (context) {
    context.commit('setSac', null)
    context.commit('reinitializeServicerSettings')
    context.commit('reinitializePartnerSettings')
  },
  /**
   * Warms POST /get-started/api/chat/create (deduped). Safe to call from LandingPage load and ConversationalChat mount.
   */
  async ensureConversationalChatSession (context) {
    const { state, commit } = context
    if (state.conversationalChatId) return
    if (!state.sac) throw new Error('No SAC')

    const existing = getChatInitInflight()
    if (existing) return existing

    const epoch = state.conversationalWarmupEpoch
    const promise = (async () => {
      for (let attempt = 1; attempt <= 3; attempt++) {
        if (state.conversationalWarmupEpoch !== epoch || state.conversationalChatId) return
        try {
          const { data } = await axios.post('/get-started/api/chat/create', {
            context: {
              origin: 'lead_gen',
              sac_code: state.sac,
              ...(state.selectedLoanType ? { loan_type: state.selectedLoanType } : {})
            }
          })
          if (state.conversationalWarmupEpoch !== epoch || state.conversationalChatId) return
          commit('setConversationalChatId', data.chat_id)
          commit('setConversationalQuestionKeys', data.question_keys)
          return
        } catch (error) {
          console.error('Failed to initialize chat:', error)
          if (attempt < 3 && state.conversationalWarmupEpoch === epoch) {
            await new Promise(resolve => setTimeout(resolve, 400 * attempt))
          }
        }
      }
      if (state.conversationalWarmupEpoch !== epoch) return
      throw new Error('Chat initialization failed after retries')
    })().finally(() => {
      if (getChatInitInflight() === promise) {
        clearChatInitInflight()
      }
    })

    setChatInitInflight(promise)
    return promise
  }
}
